const MISSING_FIELD_LABELS: Record<string, string> = {
  patient_id: 'пациент',
  authoring_clinician_id: 'врач-автор плана',
  treatment_plan_catalog_items: 'AI не нашёл подходящих услуг в номенклатуре',
}

export function formatMissingRequiredFields(fields: string[] | undefined): string[] {
  if (!fields?.length) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const field of fields) {
    const label = MISSING_FIELD_LABELS[field] ?? field
    if (!seen.has(label)) {
      seen.add(label)
      out.push(label)
    }
  }
  return out
}

export const PLAN_DIAGNOSTICS_COMPOSE_ACTION = 'plan_diagnostics_auto_compose'
export const PLAN_DIAGNOSTICS_MODEL_CHOICE = 'qwen3_6_flash' as const

export const PLAN_DIAGNOSTICS_INITIAL_PROMPT =
  'Составь план лечения по всем находкам диагностики пациента. ' +
  'Раскладывай по этапам согласно правилам этапности.'


export function isRestorableDiagnosticsProposal(proposal: unknown): boolean {
  if (!proposal || typeof proposal !== 'object') return false
  const p = proposal as { proposal_id?: string; proposed_payload?: unknown; override_payload?: unknown }
  if (!p.proposal_id || p.proposal_id.startsWith('test-')) return false
  const merged = buildMergedProposalPayload(p as Parameters<typeof buildMergedProposalPayload>[0])
  const stages = (merged as { stages?: unknown }).stages
  if (!Array.isArray(stages) || stages.length === 0) return false
  return stages.some((stage) => {
    if (!stage || typeof stage !== 'object') return false
    const items = (stage as { items?: unknown }).items
    return Array.isArray(items) && items.length > 0
  })
}

export function buildMergedProposalPayload(proposal: {
  proposed_payload?: Record<string, unknown> | null
  override_payload?: Record<string, unknown> | null
}): Record<string, unknown> {
  return {
    ...(proposal.proposed_payload ?? {}),
    ...(proposal.override_payload ?? {}),
  }
}

export function buildPlanDiagnosticsClarifyMetadata(params: {
  proposal: { proposal_id: string; proposed_payload?: Record<string, unknown> | null; override_payload?: Record<string, unknown> | null }
  staffUserId: string
}): Record<string, unknown> {
  return {
    consumer: 'staff_lk_ai',
    authoring_clinician_id: params.staffUserId,
    workspace_target: 'constructor',
    output_mode: 'staged',
    model_choice: PLAN_DIAGNOSTICS_MODEL_CHOICE,
    previous_proposal_id: params.proposal.proposal_id,
    previous_proposal_payload: buildMergedProposalPayload(params.proposal),
    clarification_round: true,
  }
}

type StagePreview = {
  key: string
  stageType: string
  clinicalObjective: string
  items: Array<{
    key: string
    teeth: string[]
    note: string | null
    snapshotId: string | null
    displayName: string | null
    findingText: string | null
    expectedCostMinor: number | null
    quantity: number
  }>
}

export function extractStagePreview(payload: unknown): StagePreview[] {
  if (!payload || typeof payload !== 'object') return []
  const stages = (payload as { stages?: unknown }).stages
  if (!Array.isArray(stages)) return []
  return stages.flatMap((stage, stageIndex) => {
    if (!stage || typeof stage !== 'object') return []
    const s = stage as Record<string, unknown>
    const itemsRaw = Array.isArray(s.items) ? s.items : []
    const items = itemsRaw.flatMap((item, itemIndex) => {
      if (!item || typeof item !== 'object') return []
      const i = item as Record<string, unknown>
      const scope = i.dental_scope
      const teeth =
        scope && typeof scope === 'object' && Array.isArray((scope as { teeth?: unknown }).teeth)
          ? ((scope as { teeth: unknown[] }).teeth.filter((t) => typeof t === 'string') as string[])
          : []
      const meta = i.metadata && typeof i.metadata === 'object' ? (i.metadata as Record<string, unknown>) : null
      const findingText =
        typeof meta?.finding_text === 'string'
          ? meta.finding_text
          : typeof meta?.catalog_hint === 'string'
            ? meta.catalog_hint
            : null
      const displayName =
        typeof i.display_name === 'string'
          ? i.display_name
          : findingText
      const qtyRaw = i.quantity
      const quantity = typeof qtyRaw === 'number' && qtyRaw > 0 ? qtyRaw : 1
      const costRaw = i.expected_cost_minor
      const expectedCostMinor =
        typeof costRaw === 'number' && Number.isFinite(costRaw) ? costRaw : null
      return [
        {
          key: `${stageIndex}-${itemIndex}`,
          teeth,
          note: typeof i.clinical_note === 'string' ? i.clinical_note : null,
          snapshotId: typeof i.snapshot_id === 'string' ? i.snapshot_id : null,
          displayName,
          findingText,
          expectedCostMinor,
          quantity,
        },
      ]
    })
    return [
      {
        key: String(stageIndex),
        stageType: typeof s.stage_type === 'string' ? s.stage_type : 'unknown',
        clinicalObjective: typeof s.clinical_objective === 'string' ? s.clinical_objective : '',
        items,
      },
    ]
  })
}

export type DiagnosticsAiProposal = {
  proposal_id: string
  status?: string
  proposed_payload?: Record<string, unknown> | null
  override_payload?: Record<string, unknown> | null
  missing_required_fields?: string[]
  metadata_payload?: Record<string, unknown> | null
  applied_mis_plan_id?: string | null
}

function rowToDiagnosticsProposal(row: Record<string, unknown>): DiagnosticsAiProposal {
  return {
    proposal_id: String(row.proposal_id ?? ''),
    status: typeof row.status === 'string' ? row.status : undefined,
    proposed_payload: (row.proposed_payload as Record<string, unknown> | null) ?? null,
    override_payload: (row.override_payload as Record<string, unknown> | null) ?? null,
    missing_required_fields: Array.isArray(row.missing_required_fields)
      ? (row.missing_required_fields.filter((f) => typeof f === 'string') as string[])
      : [],
    metadata_payload: (row.metadata_payload as Record<string, unknown> | null) ?? null,
    applied_mis_plan_id: typeof row.applied_mis_plan_id === 'string' ? row.applied_mis_plan_id : undefined,
  }
}

/** После 502/таймаута compose мог успеть завершиться в MIS — подтягиваем последний черновик. */
export async function recoverLatestDiagnosticsProposal(params: {
  apiBase: string
  authHeader: Record<string, string>
  misPatientId: string
  maxAgeMs?: number
}): Promise<DiagnosticsAiProposal | null> {
  const res = await fetch(
    `${params.apiBase}/staff/ai-assist/patients/${encodeURIComponent(params.misPatientId)}/proposals?limit=5`,
    { headers: params.authHeader },
  )
  if (!res.ok) return null
  const data = (await res.json()) as { items?: unknown[] }
  const items = Array.isArray(data.items) ? data.items : []
  const maxAgeMs = params.maxAgeMs ?? 15 * 60 * 1000
  const cutoff = Date.now() - maxAgeMs
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue
    const row = raw as Record<string, unknown>
    const meta = (row.metadata_payload as Record<string, unknown> | null) ?? {}
    const actionKey = typeof meta.action_key === 'string' ? meta.action_key : ''
    if (actionKey !== PLAN_DIAGNOSTICS_COMPOSE_ACTION) continue
    if (row.status !== 'ready_for_review') continue
    const createdAt = typeof row.created_at === 'string' ? Date.parse(row.created_at) : NaN
    if (Number.isFinite(createdAt) && createdAt < cutoff) continue
    const proposal = rowToDiagnosticsProposal(row)
    if (isRestorableDiagnosticsProposal(proposal)) return proposal
  }
  return null
}


export function isRedundantDiagnosticsNote(note: string | null | undefined, label: string): boolean {
  const raw = note?.trim()
  if (!raw) return true
  if (raw === label) return true
  const stripped = raw.includes(':') ? raw.split(':').slice(1).join(':').trim() : raw
  return stripped === label
}

export function stripToothPrefixLabel(text: string): string {
  const trimmed = text.trim()
  const match = trimmed.match(/^\d{1,2}\.\d{1,2}\s*:\s*(.+)$/)
  return match ? match[1].trim() : trimmed
}

export function formatDiagnosticsItemLabel(item: {
  note?: string | null
  displayName?: string | null
  findingText?: string | null
  snapshotId?: string | null
}): string {
  if (item.displayName?.trim()) return stripToothPrefixLabel(item.displayName.trim())
  const note = item.note?.trim()
  if (note) {
    const parts = note.split(':')
    if (parts.length > 1) return parts.slice(1).join(':').trim() || note
    return note
  }
  if (item.findingText?.trim()) return stripToothPrefixLabel(item.findingText.trim())
  return item.snapshotId?.trim() || 'Услуга'
}


export function formatDiagnosticsPriceRub(minor: number | null | undefined, quantity = 1): string | null {
  if (minor == null || !Number.isFinite(minor) || minor <= 0) return null
  const qty = quantity > 0 ? quantity : 1
  const rub = (minor * qty) / 100
  return `${rub.toLocaleString('ru-RU')} ₽`
}


export const MIS_PLAN_MATCHER_RECOMPOSE_HINT =
  'Пересобери план по актуальным правилам каталога: убери потерю кости и снижение уровня кости из позиций, ' +
  'эндо только «лечение пульпита» или «перелечивание» по числу каналов, пломбы — не виниры, ' +
  'без первичных консультаций на каждый зуб, одна основная услуга на зуб.'

export async function seedMisPlanCorrectionProposal(params: {
  apiBase: string
  authHeader: Record<string, string>
  misPatientId: string
  staffUserId: string
  sourceMisPlanId: string
  seedPayload: Record<string, unknown>
}): Promise<DiagnosticsAiProposal> {
  const res = await fetch(`${params.apiBase}/staff/ai-assist/proposals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...params.authHeader },
    body: JSON.stringify({
      action_key: PLAN_DIAGNOSTICS_COMPOSE_ACTION,
      ui_surface: 'staff_lk_mis_plan_correction',
      source_kind: 'mis_plan_correction_seed',
      input_text: 'Импорт MIS-плана для корректировки врачом и ИИ.',
      patient_id: params.misPatientId,
      metadata: {
        consumer: 'staff_lk_ai',
        authoring_clinician_id: params.staffUserId,
        workspace_target: 'constructor',
        output_mode: 'staged',
        model_choice: PLAN_DIAGNOSTICS_MODEL_CHOICE,
        source_mis_plan_id: params.sourceMisPlanId,
        seed_proposal_payload: params.seedPayload,
      },
    }),
  })
  if (!res.ok) throw new Error(`seed proposal HTTP ${res.status}`)
  const data = (await res.json()) as { proposal: DiagnosticsAiProposal }
  return data.proposal
}